import { inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { CompanyOutput, PersonOutput, Step } from '@qtek/shared/models';
import { EMPTY, of, pipe, Subject } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';

import { UserStore } from '../user/user.store';
import { CompanyService } from './company.service';
import { PersonService } from './person.service';
import { WizardService } from './wizard.service';

export interface WizardState {
  steps: Step[];
  maxPersonProgress: number | null;
  maxCompanyProgress: number | null;
}

const initialState: WizardState = {
  steps: [],
  maxPersonProgress: null,
  maxCompanyProgress: null,
};

export const WizardStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withProps(() => ({
    stepsLoaded$: new Subject<void>(),
    stepSuccess$: new Subject<void>(),
    stepFailure$: new Subject<void>(),
  })),
  withMethods(
    (
      store,
      wizardService = inject(WizardService),
      personService = inject(PersonService),
      companyService = inject(CompanyService),
      userStore = inject(UserStore)
    ) => ({
      isWizardDone(canUpdateCompany: boolean): boolean {
        const maxPerson = store.maxPersonProgress();
        const maxCompany = store.maxCompanyProgress();
        const isPersonDone =
          (userStore.personProgress() & maxPerson) === maxPerson;
        const isCompanyDone =
          !canUpdateCompany ||
          (userStore.companyProgress() & maxCompany) === maxCompany;
        return isPersonDone && isCompanyDone;
      },
      loadSteps: rxMethod<void>(
        pipe(
          switchMap(() =>
            wizardService.getSteps().pipe(
              tap(({ res }) => {
                patchState(store, {
                  maxPersonProgress: res.meta.progress[0].value,
                  maxCompanyProgress: res.meta.progress[1]
                    ? res.meta.progress[1].value
                    : 0,
                  steps: res.children,
                });
                store.stepsLoaded$.next();
              }),
              catchError(() => EMPTY)
            )
          )
        )
      ),
      updatePerson: rxMethod<{
        personId: string;
        payload: Partial<PersonOutput>;
      }>(
        pipe(
          switchMap(({ personId, payload }) =>
            personService.updatePerson(personId, payload).pipe(
              tap(({ res }) => {
                userStore.applyPersonData(res);
                store.stepSuccess$.next();
              }),
              catchError(() => {
                store.stepFailure$.next();
                return of(null);
              })
            )
          )
        )
      ),
      updateCompany: rxMethod<{
        companyId: string;
        payload: Partial<CompanyOutput>;
      }>(
        pipe(
          switchMap(({ companyId, payload }) =>
            companyService.updateCompany(companyId, payload).pipe(
              tap(({ res }) => {
                userStore.applyCompanyData(res);
                store.stepSuccess$.next();
              }),
              catchError(() => {
                store.stepFailure$.next();
                return of(null);
              })
            )
          )
        )
      ),
    })
  )
);
